/**
 * Improvement Mission sub-KPI type.
 *
 * A measured value RISING from a baseline toward a HIGHER target. The
 * score reflects progress from baseline toward target.
 *
 * Examples: test score average rising from 60 to 85, biodiversity index
 * rising from 0.4 to 0.7, customer satisfaction rising from 70% to 90%,
 * employment rate rising from 60% to 80%.
 *
 * Distinguished from Count: Improvement starts at a non-zero baseline and
 * shifts a measured value upward (test scores, indices, rates). Count
 * starts at 0 and accumulates additively (trees planted, students
 * completed). Same direction (increase = improving) but different mental
 * model and different starting-value field.
 *
 * Scoring math (symmetric to Reduction, inverted direction):
 *   - If current >= target: score = 100 (target met)
 *   - If current <= baseline: score = 0 (no progress)
 *   - Otherwise: score = round(100 × (current - baseline) / (target - baseline))
 *
 * Trend uses direction-aware delta with goal_direction='increase'.
 * Deadline degradation is configurable per-instance via
 * `deadline_degradation_enabled` (default true).
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2.
 */

import type {
  ConfigFieldSchema,
  MissionDataPoint,
  MissionSubKpiType,
  SetupExample,
  SubKpiScore,
  UiHints,
  ValidationError, // DEV-963: surfaced after TS 5.4 upgrade.
} from './types';
import { latestAsOf } from '../timeSeries';
import { applyDeadlineDegradation } from './deadline';
import { KPI_DEFAULTS } from '../defaults';
import { validateCommonCrossFields } from './commonValidations';

/**
 * Configuration for an Improvement sub-KPI instance.
 */
export interface ImprovementConfig extends Record<string, unknown> {
  /** Display name for the metric (e.g., "Test Score Average"). */
  display_name: string;
  /** Initial measured value at project start. */
  baseline_value: number;
  /** Target value to reach (must be > baseline_value). */
  target_value: number;
  /** Unit of measure for display (e.g., "points", "%"). */
  unit_of_measure: string;
  /** Optional deadline (YYYY-MM-DD) for reaching the target. */
  deadline_date?: string;
  /** Days before deadline to trigger Yellow warning. Default 30. */
  warning_days?: number;
  /** Days before deadline to trigger Red critical. Default 7. */
  critical_days?: number;
  /** Whether deadline-proximity degradation is applied. Default true. */
  deadline_degradation_enabled?: boolean;
  /** Score >= this is green. Default 80. */
  green_threshold?: number;
  /** Score >= this (and < green_threshold) is yellow. Default 60. */
  yellow_threshold?: number;
}

/* ------------------------------------------------------------------------- */
/*  Scoring                                                                  */
/* ------------------------------------------------------------------------- */

function scoreImprovement(
  config: ImprovementConfig,
  data: MissionDataPoint[],
  asOfDate: string,
): SubKpiScore {
  const { baseline_value, target_value, unit_of_measure } = config;

  const pts = data.map((d) => ({ date: d.date, value: d.value }));
  const cur = latestAsOf(pts, asOfDate);

  if (!cur) {
    return {
      score_0_100: null,
      progress_0_100: null,
      current_value: null,
      target_value,
      baseline_value,
      explain: 'No improvement data available as of this date.',
    };
  }

  const currentValue = cur.value;

  // Target met (current at or above target).
  if (currentValue >= target_value) {
    const baseScore = 100;
    const goalMet = true;
    const degraded = applyDeadlineDegradation({
      base_score: baseScore,
      goal_met: goalMet,
      asOfDate,
      config: {
        deadline_date: config.deadline_date,
        warning_days: config.warning_days,
        critical_days: config.critical_days,
        deadline_degradation_enabled: config.deadline_degradation_enabled,
      },
    });
    return {
      score_0_100: degraded.score,
      // DEV-864: At or above target → 100% raw progress regardless of degradation.
      progress_0_100: 100,
      current_value: currentValue,
      target_value,
      baseline_value,
      explain:
        `Target met: ${currentValue} ${unit_of_measure} ` +
        `at or above target ${target_value} ${unit_of_measure}.`,
    };
  }

  // No progress (current at or below baseline).
  if (currentValue <= baseline_value) {
    const degraded = applyDeadlineDegradation({
      base_score: 0,
      goal_met: false,
      asOfDate,
      config: {
        deadline_date: config.deadline_date,
        warning_days: config.warning_days,
        critical_days: config.critical_days,
        deadline_degradation_enabled: config.deadline_degradation_enabled,
      },
    });
    return {
      score_0_100: degraded.score,
      // DEV-864: At or below baseline → 0% raw progress.
      progress_0_100: 0,
      current_value: currentValue,
      target_value,
      baseline_value,
      explain:
        `No progress: current ${currentValue} ${unit_of_measure} ` +
        `at or below baseline ${baseline_value} ${unit_of_measure}.`,
    };
  }

  // Partial progress between baseline and target.
  const span = target_value - baseline_value;
  const progress = currentValue - baseline_value;
  const baseScore = Math.round(
    Math.max(0, Math.min(100, (progress / span) * 100)),
  );
  const degraded = applyDeadlineDegradation({
    base_score: baseScore,
    goal_met: false,
    asOfDate,
    config: {
      deadline_date: config.deadline_date,
      warning_days: config.warning_days,
      critical_days: config.critical_days,
      deadline_degradation_enabled: config.deadline_degradation_enabled,
    },
  });

  const baseExplain =
    `Improved from ${baseline_value} to ${currentValue} ${unit_of_measure} ` +
    `(target ${target_value} ${unit_of_measure}); ${baseScore}% of the way.`;

  return {
    score_0_100: degraded.score,
    // DEV-864: Raw progress = ((current - baseline) / (target - baseline)) * 100.
    progress_0_100: baseScore,
    current_value: currentValue,
    target_value,
    baseline_value,
    explain: degraded.explain ? `${degraded.explain} ${baseExplain}` : baseExplain,
  };
}

/* ------------------------------------------------------------------------- */
/*  Color derivation                                                         */
/* ------------------------------------------------------------------------- */

function colorFromScore(
  score: SubKpiScore,
  config: ImprovementConfig,
): 0 | 1 | 2 | 3 {
  if (score.score_0_100 === null) return 0;
  const greenAt = config.green_threshold ?? KPI_DEFAULTS.mission.bands.green_threshold;
  const yellowAt = config.yellow_threshold ?? KPI_DEFAULTS.mission.bands.yellow_threshold;
  if (score.score_0_100 >= greenAt) return 3;
  if (score.score_0_100 >= yellowAt) return 2;
  return 1;
}

/* ------------------------------------------------------------------------- */
/*  Validation                                                               */
/* ------------------------------------------------------------------------- */

function validateConfig(config: ImprovementConfig): ValidationError | null {
  // DEV-816 / Skip: per-field required checks live in the schema; the
  // duplicates here caused premature banner errors. Cross-field rule:
  // target must be greater than baseline (it's an improvement).
  if (
    Number.isFinite(config.target_value) &&
    Number.isFinite(config.baseline_value) &&
    config.target_value <= config.baseline_value
  ) {
    return {
      field: 'target_value',
      message: 'Target must be greater than baseline (this is an Improvement type).',
    };
  }
  return validateCommonCrossFields(config);
}

/* ------------------------------------------------------------------------- */
/*  Schema, examples, and UI hints                                           */
/* ------------------------------------------------------------------------- */

const config_schema: ConfigFieldSchema[] = [
  {
    key: 'display_name',
    label: 'What measure are you improving?',
    type: 'string',
    required: true,
    help_text:
      'A short name for the measure (e.g., "Test Score Average", "Biodiversity Index").',
  },
  {
    key: 'baseline_value',
    label: 'Starting (baseline) value',
    type: 'number',
    required: true,
    help_text: 'The measured value at the start of the project.',
    inline_with_next: true, // pair with target_value (DEV-816)
  },
  {
    key: 'target_value',
    label: 'Target value',
    type: 'number',
    required: true,
    help_text: 'The value you aim to reach. Must be greater than baseline.',
  },
  {
    key: 'unit_of_measure',
    label: 'Unit of measure',
    type: 'string',
    required: true,
    help_text: 'E.g., "points", "%", "index value".',
  },
  {
    key: 'tracking_start_date',
    label: 'Tracking start date (optional)',
    type: 'date',
    required: false,
    help_text: 'Scoring begins on this date. Reporting periods before this date are ignored.',
    inline_with_next: true, // pair with deadline_date (DEV-816)
  },
  {
    key: 'deadline_date',
    label: 'Target deadline (optional)',
    type: 'date',
    required: false,
    help_text: 'When the target should be reached.',
  },
  // deadline_degradation_enabled removed (DEV-814 / Skip): redundant once
  // color_mode is explicit. See count.ts for the rationale.
  // warning_days / critical_days deliberately NOT exposed in the form (DEV-814).
  // They were a redundant second pair of red/yellow controls. Deadline
  // degradation still uses internal defaults (KPI_DEFAULTS.deadline) when
  // deadline_degradation_enabled is true. The user only configures the SCORE
  // thresholds below; deadline pressure caps the score into the right band.
  {
    key: 'color_mode',
    label: 'Color the indicator from',
    type: 'enum',
    required: false,
    default_value: 'score',
    enum_options: [
      {
        value: 'score',
        label: 'Progress (% from baseline to target)',
        help: 'Color is driven by how far along we are from the starting baseline to the target. Set the % thresholds below for green/yellow/red.',
      },
      {
        value: 'deadline',
        label: 'Time remaining until deadline',
        help: 'Color is driven by days remaining: green far from deadline, yellow within the warning window, red within the critical window. Useful when the deadline urgency is the headline signal, not the % progress.',
      },
    ],
    help_text: 'Pick how the indicator color is computed.',
  },
  // Labels reframed for consistency (DEV-814 / Skip): "Yellow when X drops
  // below" pattern applied across all color modes. See count.ts for the
  // field-name-to-concept mapping rationale.
  {
    key: 'green_threshold',
    label: 'Yellow when % of progress drops below',
    type: 'number',
    required: false,
    default_value: 80,
    suffix: '%',
    help_text: 'Indicator turns yellow once the % of baseline-to-target progress drops below this number. Default 80.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
    inline_with_next: true, // pair with red threshold (DEV-816)
  },
  {
    key: 'yellow_threshold',
    label: 'Red when % of progress drops below',
    type: 'number',
    required: false,
    default_value: 60,
    suffix: '%',
    help_text: 'Indicator turns red once the % of baseline-to-target progress drops below this number. Must be less than yellow. Default 60.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
  },
  // Deadline-mode windows reframed (DEV-816 / Skip) to "drops below" pattern.
  {
    key: 'warning_days',
    label: 'Yellow when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 30,
    suffix: 'days',
    help_text: 'Indicator turns yellow once fewer than this many days remain until the deadline. Default 30.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
    inline_with_next: true, // pair with critical_days (DEV-816)
  },
  {
    key: 'critical_days',
    label: 'Red when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 7,
    suffix: 'days',
    help_text: 'Indicator turns red once fewer than this many days remain until the deadline. Must be less than yellow. Default 7.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
  },
];

const setup_examples: SetupExample[] = [
  {
    title: 'Test score improvement',
    description:
      'Improve average student test scores from baseline 60 points to target 85 ' +
      'through tutoring and curriculum changes.',
    example_config: {
      display_name: 'Test Score Average',
      baseline_value: 60,
      target_value: 85,
      unit_of_measure: 'points',
      deadline_date: '2026-12-31',
    },
  },
  {
    title: 'Biodiversity index improvement',
    description:
      'Raise the local Shannon biodiversity index from baseline 0.4 to target 0.7 ' +
      'through habitat restoration.',
    example_config: {
      display_name: 'Shannon Biodiversity Index',
      baseline_value: 0.4,
      target_value: 0.7,
      unit_of_measure: 'index value',
    },
  },
  {
    title: 'Community satisfaction improvement',
    description:
      'Raise community satisfaction survey score from baseline 70% to target 90% ' +
      'through programmatic improvements.',
    example_config: {
      display_name: 'Community Satisfaction',
      baseline_value: 70,
      target_value: 90,
      unit_of_measure: '%',
    },
  },
];

const ui_hints: UiHints = {
  default_chart: 'line',
  value_format: 'number',
  show_target_line: true,
};

/* ------------------------------------------------------------------------- */
/*  Export                                                                   */
/* ------------------------------------------------------------------------- */

export const improvementType: MissionSubKpiType<ImprovementConfig> = {
  type_key: 'improvement',
  display_name: 'Improvement',
  description:
    'Track a value that should rise from a baseline toward a higher target.',
  examples: [
    'customer satisfaction score',
    'code-review approval rate',
    'biodiversity index',
  ],
  use_when: 'How much better is it now?',
  supported_color_modes: ['score', 'deadline'],
  goal_direction: 'increase',
  config_schema,
  validate_config: validateConfig,
  score: scoreImprovement,
  color_from_score: colorFromScore,
  ui_hints,
  setup_examples,
};
